import { useComments } from '@/hooks/useComments'
import { CommentItem } from './CommentItem'
import { CommentForm } from './CommentForm'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { CommentableType } from '@/types/database'
import { MessageSquare } from 'lucide-react'

interface CommentsListProps {
  projectId: string
  commentableType: CommentableType
  commentableId: string
}

export function CommentsList({
  projectId,
  commentableType,
  commentableId,
}: CommentsListProps) {
  const { data: comments = [], isLoading, error } = useComments(commentableType, commentableId)

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-sm text-destructive">
            Failed to load comments. Please try again.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4" />
          Comments
          {!isLoading && comments.length > 0 && (
            <span className="text-muted-foreground font-normal">
              ({comments.length})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Comment form */}
        <CommentForm
          projectId={projectId}
          commentableType={commentableType}
          commentableId={commentableId}
        />

        {/* Comments list */}
        <div className="space-y-2">
          {isLoading ? (
            <CommentsListSkeleton />
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No comments yet. Be the first to comment!
            </div>
          ) : (
            <>
              {comments.map((comment) => (
                <CommentItem key={comment.id} comment={comment} />
              ))}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function CommentsListSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3 p-4">
          <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      ))}
    </div>
  )
}
